import { useEffect, useState } from 'react';
import {
  ActionsDialogue, Bouton, Champ, Dialogue, GrilleFormulaire, Message,
} from '@journal/ui';
import { messageErreur } from '../lib/session.tsx';
import type { Membre } from '../lib/api.ts';

/**
 * Corriger le nom d'un membre de l'équipe.
 *
 * ── Pourquoi c'est la direction qui le fait ─────────────────────────────────
 * Dans une école, c'est elle qui crée les comptes et qui répartit les classes.
 * L'annuaire va avec : le prénom saisi à la va-vite un jour de rentrée se
 * corrige au même endroit, sans demander à l'intéressé de s'en occuper — il ne
 * verra la coquille que le jour où il se connectera, s'il la remarque.
 *
 * Un enseignant seul, propriétaire de son propre espace, corrige le sien depuis
 * sa barre latérale : c'est le même droit, exercé là où il le lit.
 */
export function DialogueNomMembre({
  membre, surFermeture, surEnregistrement,
}: {
  /** `null` ferme le dialogue. */
  membre: Membre | null;
  surFermeture: () => void;
  surEnregistrement: (membre: Membre, prenom: string, nom: string) => Promise<void>;
}) {
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // La saisie repart de l'enregistré à chaque ouverture, et suit le membre
  // choisi : sans cela, ouvrir la fiche d'un collègue après en avoir abandonné
  // une autre proposerait le nom de la précédente.
  useEffect(() => {
    if (!membre) return;
    setPrenom(membre.firstName);
    setNom(membre.lastName);
    setErreur(null);
  }, [membre]);

  async function enregistrer() {
    if (!membre) return;
    setErreur(null);
    setEnCours(true);
    try {
      await surEnregistrement(membre, prenom.trim(), nom.trim());
      surFermeture();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Dialogue
      // L'adresse dans le titre : c'est elle qui identifie sans ambiguïté la
      // fiche qu'on est en train de corriger, précisément parce que le nom
      // qu'elle porte est peut-être le mauvais.
      titre={membre ? `Nom de ${membre.email}` : 'Nom du membre'}
      ouvert={membre !== null}
      surFermeture={surFermeture}
    >
      <div className="flex flex-col gap-4">
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <GrilleFormulaire>
          <Champ
            libelle="Prénom"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            autoComplete="off"
            maxLength={60}
            required
          />
          <Champ
            libelle="Nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            autoComplete="off"
            maxLength={60}
            required
          />
        </GrilleFormulaire>
        <ActionsDialogue>
          <Bouton variante="secondary" onClick={surFermeture}>Annuler</Bouton>
          <Bouton
            onClick={() => void enregistrer()}
            disabled={enCours || prenom.trim() === '' || nom.trim() === ''}
          >
            Enregistrer
          </Bouton>
        </ActionsDialogue>
      </div>
    </Dialogue>
  );
}
